export class AudioManager {
  private ctx: AudioContext | null = null;
  private enabled: boolean = true;
  private masterVolume: number = 0.5;
  private musicGain: GainNode | null = null;
  private initialized: boolean = false;
  private menuMusic: HTMLAudioElement;
  private gameMusic: HTMLAudioElement;
  private currentMusic: HTMLAudioElement | null = null;
  private musicVolume: number = 0.35;
  private menuAnalyser: AnalyserNode | null = null;
  private menuSource: MediaElementAudioSourceNode | null = null;

  constructor() {
    this.menuMusic = this.createMusic('/assets/music/Menu.mp3');
    this.gameMusic = this.createMusic('/assets/music/Partida.mp3');
    this.setupAutoPlay();
  }

  private createMusic(src: string): HTMLAudioElement {
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = this.musicVolume;
    audio.onended = () => {
      audio.currentTime = 0;
      void audio.play().catch(() => {});
    };
    return audio;
  }

  private setupAutoPlay(): void {
    const unlock = async () => {
      if (this.ctx?.state === 'suspended') {
        await this.ctx.resume();
      }
      if (this.currentMusic?.paused) {
        await this.currentMusic.play().catch((error) => {
          console.warn('[Audio] Music playback is still blocked:', error);
        });
      }
    };
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('touchstart', unlock, { passive: true });
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this.masterVolume * 0.3;
      this.musicGain.connect(this.ctx.destination);
      if (!this.menuAnalyser) {
        const analyser = this.ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.82;
        analyser.connect(this.ctx.destination);
        this.menuAnalyser = analyser;
        this.routeMenuMusic();
      }
    }
    return this.ctx;
  }

  private routeMenuMusic(): void {
    if (this.menuSource || !this.ctx || !this.menuAnalyser) return;
    try {
      const source = this.ctx.createMediaElementSource(this.menuMusic);
      source.connect(this.menuAnalyser);
      this.menuSource = source;
      console.info('[Audio] Menu music routed to the audio visualizer analyser.');
    } catch (error) {
      console.info('[Audio] Menu music analyser unavailable, visualizer will fall back to procedural pulse:', error);
      this.menuSource = null;
    }
  }

  getMenuAnalyser(): AnalyserNode | null {
    return this.menuAnalyser;
  }

  playImpact(): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(this.masterVolume * 0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  }

  playOrbCollect(): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(this.masterVolume * 0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  }

  playElimination(): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(300, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.5);
    gain.gain.setValueAtTime(this.masterVolume * 0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  }

  playVictory(): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.15);
      gain.gain.linearRampToValueAtTime(this.masterVolume * 0.3, ctx.currentTime + i * 0.15 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.3);
    });
  }

  playUpgradeSuccess(maxed: boolean = false): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const frequencies = maxed ? [660, 880, 1100] : [620, 860];
    frequencies.forEach((frequency, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      const start = ctx.currentTime + index * 0.055;
      gain.gain.setValueAtTime(0.001, start);
      gain.gain.linearRampToValueAtTime(this.masterVolume * 0.12, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.15);
    });
  }

  playUpgradeRejected(): void {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(170, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(130, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(this.masterVolume * 0.09, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  }

  setVolume(vol: number): void {
    this.masterVolume = Math.max(0, Math.min(1, vol));
  }

  setMusicVolume(vol: number): void {
    this.musicVolume = Math.max(0, Math.min(1, vol));
    this.menuMusic.volume = this.musicVolume;
    this.gameMusic.volume = this.musicVolume;
  }

  playMenuMusic(): void {
    this.ensureContext();
    this.playMusic(this.menuMusic);
  }

  playGameMusic(): void {
    this.playMusic(this.gameMusic);
  }

  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
      this.currentMusic = null;
    }
  }

  private playMusic(track: HTMLAudioElement): void {
    if (this.currentMusic === track && !track.paused) return;
    if (this.currentMusic && this.currentMusic !== track) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
    }
    this.currentMusic = track;
    track.volume = this.musicVolume;
    track.load();
    void track.play().catch((error) => {
      // Browsers may block audio until the first click, key or touch. The
      // unlock listeners above resume the requested track at that moment.
      console.info('[Audio] Waiting for user interaction to start music:', error);
    });
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
}
